import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Eye, 
  X, 
  Type, 
  ALargeSmall, 
  Ruler, 
  ScanLine,
  Accessibility,
  ChevronDown,
  ChevronUp,
  Settings2
} from 'lucide-react';

// Context for global accessibility settings
const AccessibilityContext = createContext(null);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

// Font definitions
const FONTS = {
  inter: {
    name: 'Inter',
    value: "'Inter', sans-serif",
    description: 'Standard'
  },
  opendyslexic: {
    name: 'OpenDyslexic',
    value: "'OpenDyslexic', 'Comic Sans MS', sans-serif",
    description: 'Per dislessia'
  },
  verdana: {
    name: 'Verdana',
    value: "'Verdana', 'Geneva', sans-serif",
    description: 'Alta leggibilità'
  }
};

/**
 * AccessibilityProvider
 * Provides global accessibility settings to the entire app
 */
export const AccessibilityProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('accessibility_settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse accessibility settings:', e);
      }
    }
    return {
      font: 'inter',
      fontSize: 18,
      highSpacing: false,
      readingRulerEnabled: false,
      caaEnabled: false,
      ttsAutoplay: false
    };
  });

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('accessibility_settings', JSON.stringify(settings));
  }, [settings]);

  // Apply global font and size styles
  useEffect(() => {
    const root = document.documentElement;
    const fontConfig = FONTS[settings.font];
    
    // Apply font family
    root.style.setProperty('--a11y-font-family', fontConfig.value);
    
    // Apply font size
    root.style.setProperty('--a11y-font-size', `${settings.fontSize}px`);
    
    // Apply high spacing
    if (settings.highSpacing) {
      root.style.setProperty('--a11y-letter-spacing', '0.1em');
      root.style.setProperty('--a11y-line-height', '2');
      root.style.setProperty('--a11y-word-spacing', '0.2em');
    } else {
      root.style.setProperty('--a11y-letter-spacing', 'normal');
      root.style.setProperty('--a11y-line-height', '1.6');
      root.style.setProperty('--a11y-word-spacing', 'normal');
    }

    // Add/remove body classes for global font changes
    document.body.classList.toggle('a11y-high-spacing', settings.highSpacing);
    document.body.classList.toggle('a11y-custom-font', settings.font !== 'inter');
    document.body.classList.toggle('a11y-opendyslexic', settings.font === 'opendyslexic');
    document.body.classList.toggle('a11y-verdana', settings.font === 'verdana');
    
    // Apply global font to body when OpenDyslexic is selected
    if (settings.font === 'opendyslexic') {
      document.body.style.fontFamily = fontConfig.value;
    } else if (settings.font === 'verdana') {
      document.body.style.fontFamily = fontConfig.value;
    } else {
      document.body.style.fontFamily = '';
    }
  }, [settings.font, settings.fontSize, settings.highSpacing]);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings({
      font: 'inter',
      fontSize: 18,
      highSpacing: false,
      readingRulerEnabled: false,
      caaEnabled: false,
      ttsAutoplay: false
    });
  }, []);

  return (
    <AccessibilityContext.Provider value={{ settings, updateSetting, resetSettings, FONTS }}>
      {children}
      {settings.readingRulerEnabled && <ReadingRuler />}
    </AccessibilityContext.Provider>
  );
};

/**
 * ReadingRuler Component
 * A horizontal bar that follows the mouse to help focus on one line
 */
const ReadingRuler = () => {
  const [position, setPosition] = useState({ y: 0 });
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setPosition({ y: e.clientY });
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsVisible(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!isVisible) return null;

  const rulerHeight = 60; // Height of the reading window

  return (
    <div className="fixed inset-0 pointer-events-none z-[9998]" data-testid="reading-ruler">
      {/* Top overlay */}
      <div
        className="absolute left-0 right-0 top-0 bg-black/40 transition-all duration-75"
        style={{ height: Math.max(0, position.y - rulerHeight / 2) }}
      />
      
      {/* Reading window (clear area) */}
      <div
        className="absolute left-0 right-0 bg-transparent border-y-2 border-yellow-400 shadow-lg"
        style={{
          top: Math.max(0, position.y - rulerHeight / 2),
          height: rulerHeight
        }}
      />
      
      {/* Bottom overlay */}
      <div
        className="absolute left-0 right-0 bottom-0 bg-black/40 transition-all duration-75"
        style={{ top: position.y + rulerHeight / 2 }}
      />
      
      {/* Helper text */}
      <div className="fixed bottom-4 right-4 bg-yellow-100 text-yellow-800 px-3 py-2 rounded-lg text-sm font-medium shadow-lg pointer-events-auto">
        Righello attivo • Premi ESC per chiudere
      </div>
    </div>
  );
};

/**
 * AccessibilityToolbar Component
 * Floating button that opens accessibility options
 */
export const AccessibilityToolbar = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { settings, updateSetting, resetSettings, FONTS } = useAccessibility();

  return (
    <>
      {/* Floating Button */}
      <div className={`fixed bottom-6 left-6 z-[9990] ${className}`}>
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className={`rounded-full h-14 w-14 shadow-xl transition-all duration-300 ${
            isOpen 
              ? 'bg-purple-600 hover:bg-purple-700 rotate-0' 
              : 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600'
          }`}
          data-testid="accessibility-toolbar-btn"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Accessibility className="w-6 h-6 text-white" />
          )}
        </Button>
        
        {/* Quick label */}
        {!isOpen && (
          <span className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full shadow">
            A11Y
          </span>
        )}
      </div>

      {/* Settings Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 left-6 z-[9989] w-80 animate-in slide-in-from-bottom-5 fade-in duration-300"
          data-testid="accessibility-panel"
        >
          <Card className="shadow-2xl border-2 border-purple-200 bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-purple-600" />
                  <span className="text-lg">Leggimi Meglio</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-8 w-8 p-0"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronUp className="w-4 h-4" />
                  )}
                </Button>
              </CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {/* Font Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Type className="w-4 h-4" />
                  Tipo di carattere
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(FONTS).map(([key, font]) => (
                    <button
                      key={key}
                      onClick={() => updateSetting('font', key)}
                      className={`p-2 rounded-lg text-xs text-center transition-all ${
                        settings.font === key
                          ? 'bg-purple-100 border-2 border-purple-400 text-purple-700'
                          : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ fontFamily: font.value }}
                      data-testid={`font-${key}-btn`}
                    >
                      <div className="font-bold">{font.name}</div>
                      <div className="text-gray-500 mt-0.5" style={{ fontFamily: 'Inter' }}>
                        {font.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Size */}
              <div className="space-y-2">
                <label className="flex items-center justify-between text-sm font-medium text-gray-700">
                  <div className="flex items-center gap-2">
                    <ALargeSmall className="w-4 h-4" />
                    Dimensione testo
                  </div>
                  <span className="text-purple-600 font-mono">{settings.fontSize}px</span>
                </label>
                <Slider
                  value={[settings.fontSize]}
                  onValueChange={(v) => updateSetting('fontSize', v[0])}
                  min={16}
                  max={32}
                  step={2}
                  className="py-2"
                  data-testid="font-size-slider"
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>16px</span>
                  <span>32px</span>
                </div>
              </div>

              {/* High Spacing Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <Ruler className="w-4 h-4" />
                  Alta spaziatura
                </label>
                <Switch
                  checked={settings.highSpacing}
                  onCheckedChange={(v) => updateSetting('highSpacing', v)}
                  data-testid="high-spacing-switch"
                />
              </div>

              {/* Reading Ruler Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <ScanLine className="w-4 h-4" />
                  Righello di lettura
                </label>
                <Switch
                  checked={settings.readingRulerEnabled}
                  onCheckedChange={(v) => updateSetting('readingRulerEnabled', v)}
                  data-testid="reading-ruler-switch"
                />
              </div>

              {/* Expanded Options */}
              {isExpanded && (
                <div className="pt-2 border-t border-gray-200 space-y-3">
                  {/* CAA Toggle */}
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                      <span className="text-lg">🖼️</span>
                      Simboli CAA
                    </label>
                    <Switch
                      checked={settings.caaEnabled}
                      onCheckedChange={(v) => updateSetting('caaEnabled', v)}
                      data-testid="caa-switch"
                    />
                  </div>

                  {/* TTS Autoplay */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                      <span className="text-lg">🔊</span>
                      Lettura automatica
                    </label>
                    <Switch
                      checked={settings.ttsAutoplay}
                      onCheckedChange={(v) => updateSetting('ttsAutoplay', v)}
                      data-testid="tts-autoplay-switch"
                    />
                  </div>

                  {/* Reset Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetSettings}
                    className="w-full mt-2"
                    data-testid="reset-settings-btn"
                  >
                    <Settings2 className="w-4 h-4 mr-2" />
                    Ripristina impostazioni
                  </Button>
                </div>
              )}

              {/* Preview */}
              <div 
                className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100"
                style={{
                  fontFamily: FONTS[settings.font].value,
                  fontSize: `${settings.fontSize}px`,
                  letterSpacing: settings.highSpacing ? '0.1em' : 'normal',
                  lineHeight: settings.highSpacing ? '2' : '1.6'
                }}
              >
                <p className="text-gray-700">
                  Anteprima del testo con le tue impostazioni.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

/**
 * AccessibleText Component
 * Wrapper that applies accessibility settings to text content
 */
export const AccessibleText = ({ children, className = '', as: Component = 'div' }) => {
  const { settings, FONTS } = useAccessibility();
  
  const style = {
    fontFamily: FONTS[settings.font].value,
    fontSize: `${settings.fontSize}px`,
    letterSpacing: settings.highSpacing ? '0.1em' : 'normal',
    lineHeight: settings.highSpacing ? '2' : '1.6',
    wordSpacing: settings.highSpacing ? '0.2em' : 'normal'
  };

  return (
    <Component className={`accessible-text ${className}`} style={style}>
      {children}
    </Component>
  );
};

export default AccessibilityToolbar;
